import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RecyclingIcon from "@mui/icons-material/Recycling";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

const RECYCLE_RESOURCES = [
  { key: "shops", label: "Shops", endpoint: "shop/shop" },
  { key: "categories", label: "Categories", endpoint: "shop/categories" },
  { key: "products", label: "Products", endpoint: "shop/products" },
  { key: "suppliers", label: "Suppliers", endpoint: "shop/suppliers" },
  { key: "customers", label: "Customers", endpoint: "shop/customers" },
  { key: "units", label: "Units", endpoint: "shop/units" },
  { key: "currencies", label: "Currencies", endpoint: "shop/currencies" },
  { key: "exchange_rates", label: "Exchange Rates", endpoint: "shop/exchange-rates" },
  { key: "stocks", label: "Stock", endpoint: "shop/stocks" },
  { key: "purchases", label: "Purchases", endpoint: "shop/purchases" },
  { key: "purchase_payments", label: "Purchase Payments", endpoint: "shop/purchase-payments" },
  { key: "sales", label: "Sales", endpoint: "shop/sales" },
  { key: "sale_payments", label: "Sale Payments", endpoint: "shop/sale_payments" },
  { key: "sale_returns", label: "Sale Returns", endpoint: "shop/sale-returns" },
  { key: "purchase_returns", label: "Purchase Returns", endpoint: "shop/purchase-returns" },
  { key: "customer_balance_payments", label: "Customer Balance Payments", endpoint: "shop/customer-balance-payments" },
  { key: "employees", label: "Employees", endpoint: "shop/employees" },
  { key: "advances", label: "Advances", endpoint: "shop/advances" },
  { key: "payrolls", label: "Payrolls", endpoint: "shop/payrolls" },
  { key: "expenses", label: "Expenses", endpoint: "shop/expenses" },
  { key: "expense_categories", label: "Expense Categories", endpoint: "shop/expense-categories" },
  { key: "incomes", label: "Incomes", endpoint: "shop/incomes" },
  { key: "accounts", label: "Accounts", endpoint: "shop/accounts" },
  { key: "account_transactions", label: "Account Transactions", endpoint: "shop/account-transactions" },
  { key: "account_transfers", label: "Account Transfers", endpoint: "shop/account-transfers" },
];

const getRecordName = (record) =>
  record?.name ||
  record?.title ||
  record?.reference ||
  record?.code ||
  record?.description ||
  record?.note ||
  `#${record?.id}`;

const RecycleBinIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const headers = useMemo(
    () => auth?.buildAuthHeader?.() || (token ? { Authorization: `Token ${token}` } : {}),
    [auth, token]
  );

  const apiBase = process.env.REACT_APP_API_BASE_URL;

  const loadRecycleBin = useCallback(async () => {
    setLoading(true);
    try {
      const responses = await Promise.allSettled(
        RECYCLE_RESOURCES.map(async (resource) => {
          const response = await axios.get(
            `${apiBase}/${resource.endpoint}/recycle-bin/?page_size=all`,
            { headers }
          );
          const data = Array.isArray(response.data)
            ? response.data
            : response.data?.results || [];
          return data.map((record) => ({ ...record, _resource: resource }));
        })
      );

      setRows(
        responses
          .flatMap((result) => (result.status === "fulfilled" ? result.value : []))
          .sort((a, b) => String(b.deleted_at || "").localeCompare(String(a.deleted_at || "")))
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, headers]);

  useEffect(() => {
    loadRecycleBin();
  }, [loadRecycleBin]);

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setSeverity(type);
  };

  const restoreRecord = async (row) => {
    await axios.post(`${apiBase}/${row._resource.endpoint}/${row.id}/restore/`, {}, { headers });
    showMessage(t("recycleBin.restoreSuccess", "Record restored successfully"));
    loadRecycleBin();
  };

  const permanentDeleteRecord = async (row) => {
    if (!window.confirm(t("recycleBin.confirmPermanentDelete", "Permanently delete this record? This cannot be undone."))) {
      return;
    }
    await axios.delete(`${apiBase}/${row._resource.endpoint}/${row.id}/permanent-delete/`, { headers });
    showMessage(t("recycleBin.permanentDeleteSuccess", "Record permanently deleted"));
    loadRecycleBin();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <RecyclingIcon color="primary" />
        <Typography variant="h4">{t("recycleBin.title", "Recycle Bin")}</Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("recycleBin.type", "Type")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("recycleBin.id", "ID")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("recycleBin.record", "Record")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("recycleBin.deletedAt", "Deleted At")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("recycleBin.actions", "Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t("recycleBin.empty", "Recycle bin is empty")}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={`${row._resource.key}-${row.id}`} hover>
                  <TableCell>
                    <Chip label={row._resource.label} size="small" />
                  </TableCell>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{getRecordName(row)}</TableCell>
                  <TableCell>{row.deleted_at || "-"}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RestoreIcon />}
                      onClick={() => restoreRecord(row)}
                      sx={{ mr: 1 }}
                    >
                      {t("recycleBin.restore", "Restore")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteForeverIcon />}
                      onClick={() => permanentDeleteRecord(row)}
                    >
                      {t("recycleBin.permanentDelete", "Permanent Delete")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={Boolean(message)} autoHideDuration={3000} onClose={() => setMessage("")}>
        <Alert severity={severity} onClose={() => setMessage("")}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RecycleBinIndex;
